<template>
  <!-- Pairs of columns over the MT morphemes both render; every count opens its evidence. -->
  <table class="agreement">
    <thead>
      <tr>
        <th>Columns</th>
        <th title="MT morphemes both render">Both</th>
        <th title="One reading attributed to both">Joint</th>
        <th title="Separate readings, a word in common">Same</th>
        <th title="Nothing in common">Different</th>
        <th title="Agree where the LXX renders otherwise">vs LXX</th>
        <th title="Of separately attested readings, the share with a word in common">Agree</th>
        <th title="Apparatus lemmas with readings of both">Lemmas</th>
      </tr>
    </thead>
    <tbody>
      <template v-for="p in pairs" :key="`${p.a}|${p.b}`">
        <tr>
          <td class="pair font-greek">{{ p.a }} · {{ p.b }}</td>
          <td class="n">{{ p.shared }}</td>
          <td v-for="r in counted" :key="r" class="n">
            <button v-if="p[r]" class="count" :class="{ 'is-open': isOpen(p, r) }" @click="toggle(p, r)">{{ p[r] }}</button>
            <span v-else class="zero">0</span>
          </td>
          <td class="n share">{{ percent(p.independent_agreement) }}</td>
          <td class="n muted">{{ p.co_attested }}</td>
        </tr>
        <tr v-if="open && open.a === p.a && open.b === p.b" class="evidence-row">
          <td colspan="8">
            <EvidenceList :key="`${p.a}|${p.b}|${open.relation}`" :a="p.a" :b="p.b" :relation="open.relation" :scope="scope" />
          </td>
        </tr>
      </template>
    </tbody>
  </table>
</template>

<script lang="ts">
import { defineComponent, PropType, ref } from 'vue'
import { Pair, Relation, Scope } from '../api'
import { percent } from '../format'
import EvidenceList from './EvidenceList.vue'

type Counted = 'joint' | 'same' | 'different' | 'against_lxx'

export default defineComponent({
  name: 'AgreementTable',
  components: { EvidenceList },
  props: {
    pairs: { type: Array as PropType<Pair[]>, required: true },
    scope: { type: Object as PropType<Scope>, default: () => ({}) },
  },
  setup() {
    const counted: Counted[] = ['joint', 'same', 'different', 'against_lxx']
    const open = ref<{ a: string; b: string; relation: Relation } | null>(null)
    const isOpen = (p: Pair, r: Counted) => open.value?.a === p.a && open.value?.b === p.b && open.value?.relation === r
    const toggle = (p: Pair, r: Counted) => (open.value = isOpen(p, r) ? null : { a: p.a, b: p.b, relation: r })
    return { counted, open, isOpen, toggle, percent }
  },
})
</script>

<style lang="scss" scoped>
@import '@/_colors';

.agreement {
  width: 100%;
  border-collapse: collapse;
  font-size: 0.85rem;

  th {
    text-align: right;
    font-weight: normal;
    color: $text-light;
    font-size: 0.75rem;
    padding: 0.2rem 0.4rem;
    border-bottom: 1px solid $border-strong;
    cursor: help;

    &:first-child {
      text-align: left;
    }
  }

  td {
    padding: 0.2rem 0.4rem;
    border-bottom: 1px solid $border;
  }

  .pair {
    white-space: nowrap;
    color: $text-dark;
  }

  .n {
    text-align: right;
    font-variant-numeric: tabular-nums;
  }

  .share {
    color: $text-dark;
    font-weight: 600;
  }

  .zero,
  .muted {
    color: $text-light;
  }

  .evidence-row td {
    background: $surface-raised;
  }
}

.count {
  border: 1px solid $border;
  border-radius: 3px;
  background: $surface;
  color: $interactive;
  cursor: pointer;
  padding: 0 0.35rem;
  font-variant-numeric: tabular-nums;

  &:hover,
  &.is-open {
    background: $interactive-soft;
  }
}
</style>
